import React from 'react';
import moment from 'moment';

const months = {
  es: {
    abbr: ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"],
    full: ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"]
  },
  en: {
    abbr: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
    full: ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"],
  }
};

const formatNumber = (number) => {
  return number?.toString()?.padStart(3, '0');
}

const toSnakeCase = (str) => {
  const removeAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return removeAccents(str).toLowerCase().replace(/\s+/g, '-');
}

const toCamelCase = (str) => {
  const removeAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  let words = removeAccents(str).split(/[\s_-]+/);
  return words.map((word, index) => {
    if (index === 0)
      return word.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join("");
}

const getDateFormats = () => {

  const separators = ["/", "-", ".", "", " "]
  const dateFormats = [
    // 2025-09-12T00:00:00.000000+00:00
    "YYYYMMDDHHmmss",
    "YYYY-MM-DD",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DDTHH:mm:ss.SSSSSS",
    "YYYY-MM-DDTHH:mm:ss.SSSSSSZ",
    "DD-MM-YYYY",
    "DD-MM-YYYYTHH:mm:ss.SSSZ",
    "DD-MM-YYYYTHH:mm:ss.SSS",
    "DD-MM-YYYYTHH:mm:ss",
    "DD-MM-YYYY HH:mm:ss",
    "DD-MM-YYYY HH:mm",
    "MM-DD-YYYY",
    "MM-DD-YYYYTHH:mm:ss.SSSZ",
    "MM-DD-YYYYTHH:mm:ss.SSS",
    "MM-DD-YYYYTHH:mm:ss",
    "MM-DD-YYYY HH:mm:ss",
    "MM-DD-YYYY HH:mm",
    "YYYY-MM-DDTHH:mm",
    "DD-MMM-YYYY",
  ];
  const formats = [];
  dateFormats.forEach((format) => {
    separators.forEach((separator) => {
      formats.push(format.replace(/-/g, separator));
    });
  });

  return formats;

}

const normalizeMonth = (value) => {

  if (!value)
    return value;

  const upper = value.toUpperCase();

  for (const locale of Object.keys(months)) {

    const { abbr = [], full = [] } = months[locale];

    for (let i = 0; i < 12; i++) {
      if (upper.includes(abbr[i])) return upper.replace(abbr[i], months.en.abbr[i]);
      if (upper.includes(full[i])) return upper.replace(full[i], months.en.abbr[i]);
    }

  }

  return value.toLowerCase();

}

const getDate = (value, format = "DD/MM/YYYY") => {

  const date = parseToMomentDate(value);
  if (!date) return "";

  const isoFormat = "YYYY-MM-DDTHH:mm:ss.SSSSSSZ";
  if (format == "iso") return date.format(isoFormat);
  return date.format(format);

}

const parseToMomentDate = (value) => {

  if (!value) return null;

  if (value instanceof Date)
    return moment.utc(value);

  if (moment(value, moment.ISO_8601, true).isValid())
    return moment.utc(value);

  const formats = getDateFormats();
  let date = null;

  value = normalizeMonth(value?.toString()?.trim()?.toUpperCase());
  value = value.replace(/^[-/.`'"]*/, "").replace(/[-/.`'"]*$/, "");

  for (const month of months.en.abbr) {
    if (value == month)
      return moment.utc(`01/${month}/${moment().year()}`);
  }

  for (let i = 0; i < 12; i++) {
    if (value.includes(months.en.abbr[i]))
      value = value.replace(
        months.en.abbr[i],
        (i + 1).toString().padStart(2, "0")
      );
  }

  for (let i = 0; i < formats.length; i++) {
    date = moment.utc(value, formats[i], true);
    if (date.isValid()) break;
  }

  if (date && !date.isValid()) return null;
  return date;

}

const getObjectComponent = (value) => {

  // if value is a list, return a list with the list data
  if (value instanceof Array) {
    return <ul className="list-group">
      {
        value.map((item, i) => (
          <li key={i} className="list-group-item">
            {getObjectComponent(item)}
          </li>
        ))
      }
    </ul>
  }

  // if value is an object, return a table with the object data

  if (value instanceof Object) {
    return <table className="table-sm w-100">
      <thead>
        <tr>
          <th style={{ minWidth: "80px" }}>Llave</th>
          <th style={{ minWidth: "80px" }}>Valor</th>
        </tr>
      </thead>
      <tbody>
        {
          Object?.keys(value).map((key, i) => (
            <tr key={key}>
              <td>{key}</td>
              <td>{getObjectComponent(value[key])}</td>
            </tr>
          ))
        }
      </tbody>
    </table>
  }

  if (value == null || value == undefined) return "";
  if (value == "") return "";

  // if value is boolean or matches with True or False, return a boolean Si/No
  if (value == true || value == false
    || ["true", "false"].includes(value?.toString().toLowerCase()))
    return value == true || value?.toString().toLowerCase() == "true" ? "Si" : "No";

  // if value is a date, return the date in format DD/MM/YYYY
  if (value instanceof Date) return getDate(value, "DD/MM/YYYY");

  // if value is a number, return the number
  if (!isNaN(value)) return value;

  // if value is a string, return the string
  return value;

}

export {
  formatNumber,
  toCamelCase,
  toSnakeCase,
  getDateFormats,
  getDate,
  getObjectComponent
}